import { useMutateCommunityTemplateLike } from '@/hooks/mutation/useMutateCommunityTemplateLike';
import { useQueryCommunityTemplateLikeStatus } from '@/hooks/query/useQueryCommunityTemplateLikeStatus';
import { useIsAuthenticated } from '@/hooks/useIsAuthenticated';
import { cn } from '@/lib/cn';
import { Button } from '@heidi/design-system/button';
import { Heart } from 'lucide-react';
import { useState } from 'react';
import { useIntl } from 'react-intl';

interface LikeButtonProps {
  templateId: string;
  likesCount?: number;
  size?: 'sm' | 'md';
  showCount?: boolean;
  className?: string;
  onLikeChange?: (liked: boolean, count: number) => void;
}

export function LikeButton({
  templateId,
  likesCount: initialLikesCount = 0,
  size = 'md',
  showCount = true,
  className,
  onLikeChange,
}: LikeButtonProps) {
  const intl = useIntl();
  const { isAuthenticated } = useIsAuthenticated();
  const { data: likeStatus } = useQueryCommunityTemplateLikeStatus(templateId);
  const [likesCount, setLikesCount] = useState(initialLikesCount);
  const { mutate, isPending } = useMutateCommunityTemplateLike({
    templateId,
    onSuccess: (data) => {
      setLikesCount(data.likes_count);
      onLikeChange?.(data.liked, data.likes_count);
    },
  });

  const isLiked = likeStatus?.liked ?? false;

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();

    if (!isAuthenticated) {
      // Could show login prompt here
      return;
    }

    mutate({ liked: isLiked });
  };

  const iconSize = size === 'sm' ? 14 : 18;

  return (
    <Button
      variant="ghost"
      size={size}
      flush="start"
      className={cn(
        'gap-1.5 text-text-secondary hover:text-text-primary',
        isLiked && 'text-accent-red hover:text-accent-red',
        className
      )}
      onClick={handleClick}
      disabled={isPending || !isAuthenticated}
      aria-label={
        isLiked
          ? intl.formatMessage({ id: 'gYFII3UAhE', defaultMessage: 'Unlike template' })
          : intl.formatMessage({ id: 'AWTyqS1U9a', defaultMessage: 'Like template' })
      }
    >
      <Heart
        size={iconSize}
        className={cn(
          'transition-all duration-200',
          isLiked && 'fill-current',
          isPending && 'animate-pulse'
        )}
      />
      {showCount && <span className="text-sm tabular-nums">{likesCount}</span>}
    </Button>
  );
}
